import { JitsiMeeting } from '@jitsi/react-sdk';
import {
    Chat,
    CloseRounded,
    FavoriteBorderRounded,
    FavoriteRounded,
    MicOffRounded,
    MicRounded,
    Share,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    ImageList,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { capitalCase } from 'change-case';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ShowMoreText from 'react-show-more-text';
import {
    useDeleteListDataMutation,
    useGetDataQuery,
    useUpdateListDataMutation,
} from '../../api/apiSlice';
import ChatSheet from '../../components/crusade/ChatSheet';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import useAuth from '../../hooks/useAuth';
import errorSnackbar from '../../utils/errorSnackbar';
import { shareCrusade } from '../../utils/sharePost';

function CrusadeRouteScreen() {
    const [jitsiApi, setJitsiApi] = useState(null);
    const [mic, setMic] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);

    const { enqueueSnackbar } = useSnackbar();
    const { id } = useParams();
    const { user, isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();

    const [joinCrusade] = useUpdateListDataMutation();
    const [leaveCrusade] = useDeleteListDataMutation();

    const { data: { data: crusade } = {}, refetch } = useGetDataQuery(
        { list: `/crusades/${id}` },
        { refetchOnMountOrArgChange: true }
    );

    const { data: { data: listeners = [] } = {}, refetch: refetchListeners } =
        useGetDataQuery(
            { list: `/crusades/${id}/listeners` },
            { refetchOnMountOrArgChange: true }
        );

    const {
        church,
        description,
        media,
        owner,
        pastor,
        title,
        speakers = [],
        interactions,
        meeting_id,
    } = crusade || {};

    const operator = owner == 'PASTOR' ? pastor : church;

    const [liked, setLiked] = useState(interactions?.liked);
    const [likes, setLikes] = useState(interactions?.likes);

    const onLike = () => {
        liked ? setLikes(likes - 1) : setLikes(likes + 1);
        setLiked(!liked);
    };

    useEffect(() => {
        !isLoggedIn && navigate('/guest-profile');
    }, [isLoggedIn, navigate]);

    useEffect(() => {
        jitsiApi?.addListener('participantJoined', () => {
            refetch();
            refetchListeners();
        });

        return () => jitsiApi?.removeListener('participantJoined', () => {});
    }, [jitsiApi, refetch, refetchListeners]);

    useEffect(() => {
        (async () => {
            const { data, error } = await joinCrusade({
                list: `/listeners/crusades/${id}`,
            });

            errorSnackbar(error, enqueueSnackbar);
            if (data) {
                enqueueSnackbar('Crusade Joined');
                refetchListeners();
            }
        })();
    }, [enqueueSnackbar, id, joinCrusade, refetchListeners]);

    const isSpeaker =
        speakers?.filter(({ id: uid }) => uid == user?.id).length > 0;

    return (
        <ExternalScreen
            loading={!jitsiApi}
            title="Crusade"
            authPass
            disablePadding
            disableSpacing
            controls={
                <IconButton
                    color="primary"
                    onClick={async () => {
                        const { data, error } = await leaveCrusade({
                            list: `/listeners/crusades/${id}`,
                        });

                        errorSnackbar(error, enqueueSnackbar);
                        if (data) enqueueSnackbar('Crusade Left');
                        navigate('/crusade');
                    }}
                >
                    <CloseRounded />
                </IconButton>
            }
        >
            <ChatSheet
                open={chatOpen}
                jitsiApi={jitsiApi}
                onClose={() => setChatOpen(false)}
            />
            <Grid item xs={12}>
                <Box>
                    {meeting_id && (
                        <JitsiMeeting
                            userInfo={{ displayName: user?.username }}
                            interfaceConfigOverwrite={{
                                APP_NAME: 'my Kanisa',
                                SHOW_JITSI_WATERMARK: false,
                                HIDE_DEEP_LINKING_LOGO: true,
                                SHOW_BRAND_WATERMARK: false,
                                SHOW_WATERMARK_FOR_GUESTS: false,
                                SHOW_POWERED_BY: false,
                                SHOW_PROMOTIONAL_CLOSE_PAGE: false,
                                SHOW_CHROME_EXTENSION_BANNER: false,
                                TOOLBAR_BUTTONS: [],
                                TOOLBAR_ALWAYS_VISIBLE: true,
                                TOOLBAR_BUTTONS_WITH_LABELS: false,
                                DEFAULT_BACKGROUND:
                                    theme.palette.background.default,
                            }}
                            // domain={`meet.jit.si/${meeting_id}#userInfo.displayName="${user?.username}"&userInfo.email="${user?.contact?.email}"&config.prejoinPageEnabled=false&config.requireDisplayName=false&disableDeepLinking=false`}
                            roomName={meeting_id}
                            getIFrameRef={(node) => (node.style.height = `2px`)}
                            onApiReady={(externalApi) =>
                                setJitsiApi(externalApi)
                            }
                            configOverwrite={{
                                prejoinPageEnabled: false,
                                startWithVideoMuted: true,
                                disableDeepLinking: true,
                                startWithAudioMuted: true,
                            }}
                        />
                    )}
                </Box>
                <CardContent>
                    <div className="d-flex align-items-center">
                        <Avatar src={operator?.avatar}>
                            {operator?.name?.slice(0, 2)}
                        </Avatar>
                        <div className="ms-2">
                            <Typography>{operator?.name}</Typography>
                            <Typography
                                variant="body2"
                                sx={{ lineHeight: 0.8 }}
                            >
                                {capitalCase(owner || '')}
                            </Typography>
                        </div>
                    </div>
                </CardContent>
                <CardContent
                    sx={{
                        py: 0,
                        mx: 2,
                        mb: 2,
                        borderRadius: 1,
                        width: window.innerWidth - 32,
                        height: window.innerWidth * 0.6,
                        backgroundColor: '#ddd',
                        backgroundImage: `url(${media?.link})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                ></CardContent>
                <CardContent sx={{ mx: 1 }}>
                    <Typography
                        style={{ textTransform: 'capitalize' }}
                        className="fw-bold mb-1"
                    >
                        {title?.toLowerCase()}
                    </Typography>
                    <ShowMoreText lines={2} more="more" less="less">
                        {description}
                    </ShowMoreText>
                </CardContent>
                <CardContent>
                    <Card variant="outlined">
                        <CardContent>
                            <ImageList
                                sx={{
                                    gridAutoFlow: 'column',
                                    gridTemplateColumns: `repeat(auto-fill,minmax(${
                                        window.innerWidth / 4
                                    }px,1fr)) !important`,
                                    gridAutoColumns: `minmax(${
                                        window.innerWidth / 4
                                    }px, 1fr)`,
                                }}
                            >
                                {[...speakers, ...listeners]?.map(
                                    ({ username, role, avatar, links }) => (
                                        <Stack
                                            alignItems="center"
                                            key={
                                                username + Math.random() * 1000
                                            }
                                        >
                                            <Avatar
                                                src={avatar || links?.avatar}
                                            />
                                            <Typography variant="body2">
                                                {username.slice(0, 6) + '...'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className="text-capitalize"
                                            >
                                                ({role || 'listener'})
                                            </Typography>
                                        </Stack>
                                    )
                                )}
                            </ImageList>
                        </CardContent>
                    </Card>
                    <Stack direction="row" justifyContent="end" sx={{ mb: 1 }}>
                        <Button>{interactions?.listening} Tuned in</Button>
                    </Stack>
                </CardContent>
            </Grid>
            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                    p: 1,
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    position: 'fixed',
                    bgcolor: 'background.default',
                    borderTop: '1px solid ',
                    borderTopColor: 'divider',
                }}
            >
                <Stack direction="row">
                    {isSpeaker && (
                        <IconButton
                            sx={{ mr: 1 }}
                            color="primary"
                            onClick={() => {
                                jitsiApi &&
                                    jitsiApi.executeCommand('toggleAudio');
                                jitsiApi && setMic(!mic);
                            }}
                        >
                            {mic ? <MicRounded /> : <MicOffRounded />}
                        </IconButton>
                    )}
                    <IconButton
                        onClick={() => setChatOpen(!chatOpen)}
                        color="primary"
                    >
                        <Chat />
                    </IconButton>
                </Stack>
                <Stack direction="row">
                    <Button
                        onClick={onLike}
                        color="secondary"
                        startIcon={
                            liked ? (
                                <FavoriteRounded />
                            ) : (
                                <FavoriteBorderRounded />
                            )
                        }
                    >
                        {likes}
                    </Button>
                    <IconButton
                        onClick={() => shareCrusade(crusade)}
                        color="primary"
                    >
                        <Share />
                    </IconButton>
                </Stack>
            </Stack>
        </ExternalScreen>
    );
}

export default CrusadeRouteScreen;
